import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
  clearCache,
  type DatasetUpdater,
  getDatastores,
  triggerDatasetCheck,
} from "admin/rest_api";
import { useEffectOnlyOnce, usePrevious, useWkSelector } from "libs/react_hooks";
import UserLocalStorage from "libs/user_local_storage";
import last from "lodash-es/last";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type {
  APIDatasetCompact,
  APIDatasetCompactWithoutStatusAndLayerNames,
  APIMaybeUnimportedDataset,
  FolderItem,
} from "types/api_types";
import {
  useCreateFolderMutation,
  useDatasetSearchQuery,
  useDatasetsInFolderQuery,
  useDeleteFolderMutation,
  useFolderHierarchyQuery,
  useFolderQuery,
  useMoveFolderMutation,
  useUpdateDatasetMutation,
  useUpdateFolderMutation,
} from "./queries";

// Describes which folder modal is currently open (if any). A single one is open at a time,
// either to create a new folder below a parent or to edit an existing folder. This lives in the
// context (rather than being prop-drilled) because the triggers are spread across the dataset
// view, the folder tree and the details sidebar. The modal itself is rendered by
// DatasetFolderView, so the context never imports the modal component (which would be cyclic).
export type FolderModalState =
  | { mode: "edit"; folderId: string }
  | { mode: "create"; parentFolderId: string };

export type DatasetCollectionContextValue = {
  datasets: Array<APIDatasetCompact>;
  isLoading: boolean;
  isChecking: boolean;
  checkDatasets: (organizationId: string | undefined) => Promise<void>;
  fetchDatasets: () => void;
  clearCacheAndReloadDataset: (datasetId: string) => Promise<void>;
  updateCachedDataset: (
    datasetId: string,
    updater: DatasetUpdater,
  ) => Promise<APIMaybeUnimportedDataset>;
  activeFolderId: string | null;
  setActiveFolderId: (id: string | null) => void;
  mostRecentlyUsedActiveFolderId: string | null;
  selectedDatasets: APIDatasetCompact[];
  selectedFolder: FolderItem | null;
  setSelectedFolder: (arg0: FolderItem | null) => void;
  setSelectedDatasets: React.Dispatch<React.SetStateAction<APIDatasetCompact[]>>;
  globalSearchQuery: string | null;
  setGlobalSearchQuery: (val: string | null) => void;
  searchRecursively: boolean;
  setSearchRecursively: (val: boolean) => void;
  getBreadcrumbs: (dataset: APIDatasetCompactWithoutStatusAndLayerNames) => string[] | null;
  getActiveSubfolders: () => FolderItem[];
  folderModalState: FolderModalState | null;
  setFolderModalState: (state: FolderModalState | null) => void;
  queries: {
    folderHierarchyQuery: ReturnType<typeof useFolderHierarchyQuery>;
    datasetsInFolderQuery: ReturnType<typeof useDatasetsInFolderQuery>;
    datasetSearchQuery: ReturnType<typeof useDatasetSearchQuery>;
    createFolderMutation: ReturnType<typeof useCreateFolderMutation>;
    updateFolderMutation: ReturnType<typeof useUpdateFolderMutation>;
    moveFolderMutation: ReturnType<typeof useMoveFolderMutation>;
    deleteFolderMutation: ReturnType<typeof useDeleteFolderMutation>;
    updateDatasetMutation: ReturnType<typeof useUpdateDatasetMutation>;
  };
  usedStorageInOrga: number | undefined;
};

const DatasetCollectionContext = createContext<DatasetCollectionContextValue | undefined>(
  undefined,
);

export const useDatasetCollectionContext = () => {
  const context = useContext(DatasetCollectionContext);
  if (!context)
    throw new Error(
      "No DatasetCollectionContext.Provider found when calling useDatasetCollectionContext.",
    );
  return context;
};

const ACTIVE_FOLDER_ID_STORAGE_KEY = "activeFolderId";

export default function DatasetCollectionContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [activeFolderId, setActiveFolderId] = useState<string | null>(
    UserLocalStorage.getItem(ACTIVE_FOLDER_ID_STORAGE_KEY) || null,
  );
  const [mostRecentlyUsedActiveFolderId, clearMostRecentlyUsedActiveFolderId] = usePrevious(
    activeFolderId,
    true,
  );
  const [isChecking, setIsChecking] = useState(false);
  const isMutating = useIsMutating() > 0;
  const { data: folder, isError: didFolderLoadingError } = useFolderQuery(activeFolderId);
  const usedStorageInOrga = useWkSelector((state) => state.activeOrganization?.usedStorageBytes);

  const [selectedDatasets, setSelectedDatasets] = useState<APIDatasetCompact[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<FolderItem | null>(null);
  const [folderModalState, setFolderModalState] = useState<FolderModalState | null>(null);
  const [globalSearchQuery, setGlobalSearchQueryInner] = useState<string | null>(null);
  const setGlobalSearchQuery = useCallback((value: string | null) => {
    // Empty string should be handled as null
    setGlobalSearchQueryInner(value ? value : null);
  }, []);
  const [searchRecursively, setSearchRecursively] = useState<boolean>(true);
  const queryClient = useQueryClient();

  // Keep url GET parameters in sync with search and active folder
  useManagedUrlParams(
    setGlobalSearchQuery,
    setActiveFolderId,
    globalSearchQuery,
    activeFolderId,
    searchRecursively,
    setSearchRecursively,
  );

  useEffect(() => {
    // Persist last active folder to localStorage. We
    // check folder against null to avoid that invalid ids are persisted.
    if (activeFolderId != null && folder != null && !didFolderLoadingError) {
      UserLocalStorage.setItem(ACTIVE_FOLDER_ID_STORAGE_KEY, activeFolderId);
    } else {
      UserLocalStorage.removeItem(ACTIVE_FOLDER_ID_STORAGE_KEY);
    }

    if (didFolderLoadingError) {
      setActiveFolderId(null);
      clearMostRecentlyUsedActiveFolderId();
      if (!folderHierarchyQuery.isFetching) {
        queryClient.invalidateQueries({ queryKey: ["folders"] });
      }
    }
  }, [
    folder,
    activeFolderId,
    didFolderLoadingError,
    clearMostRecentlyUsedActiveFolderId,
    queryClient,
  ]);

  const folderHierarchyQuery = useFolderHierarchyQuery();

  // A selected subfolder only makes sense within the active folder.
  // biome-ignore lint/correctness/useExhaustiveDependencies: Only clear when the active folder changes.
  useEffect(() => {
    setSelectedFolder(null);
  }, [activeFolderId]);

  // Clear the selected subfolder once it's gone (e.g., after deleting it).
  useEffect(() => {
    const itemById = folderHierarchyQuery.data?.itemById;
    if (selectedFolder != null && itemById != null && itemById[selectedFolder.key] == null) {
      setSelectedFolder(null);
    }
  }, [folderHierarchyQuery.data, selectedFolder]);
  const datasetsInFolderQuery = useDatasetsInFolderQuery(
    globalSearchQuery == null ? activeFolderId : null,
  );
  const datasetSearchQuery = useDatasetSearchQuery(
    globalSearchQuery,
    activeFolderId,
    searchRecursively,
  );
  const createFolderMutation = useCreateFolderMutation();
  const deleteFolderMutation = useDeleteFolderMutation();
  const updateFolderMutation = useUpdateFolderMutation();
  const moveFolderMutation = useMoveFolderMutation();
  const updateDatasetMutation = useUpdateDatasetMutation(
    globalSearchQuery == null ? activeFolderId : null,
  );
  const datasets = (globalSearchQuery ? datasetSearchQuery.data : datasetsInFolderQuery.data) || [];

  function fetchDatasets(): void {
    datasetsInFolderQuery.refetch();
    datasetSearchQuery.refetch();
  }

  async function clearCacheAndReloadDataset(datasetId: string) {
    const dataset = await updateDatasetMutation.mutateAsync(datasetId);
    await clearCache(dataset);
  }

  async function updateCachedDataset(datasetId: string, updater: DatasetUpdater) {
    return await updateDatasetMutation.mutateAsync([datasetId, updater]);
  }

  const getBreadcrumbs = (dataset: APIDatasetCompactWithoutStatusAndLayerNames) => {
    if (folderHierarchyQuery.data?.itemById == null) {
      return null;
    }
    const { itemById } = folderHierarchyQuery.data;

    let currentFolder = itemById[dataset.folderId];
    if (currentFolder == null) {
      console.warn("Breadcrumbs could not be computed.");
      return [];
    }
    const breadcrumbs = [currentFolder.title];
    while (currentFolder?.parent != null) {
      currentFolder = itemById[currentFolder.parent];
      if (currentFolder == null) {
        console.warn("Breadcrumbs could not be computed.");
        return [];
      }
      breadcrumbs.unshift(currentFolder.title);
    }

    return breadcrumbs;
  };

  const getActiveSubfolders = () => {
    return folderHierarchyQuery.data?.itemById[activeFolderId ?? ""]?.children || [];
  };

  const isLoading =
    (globalSearchQuery
      ? datasetSearchQuery.isFetching
      : folderHierarchyQuery.isPending ||
        datasetsInFolderQuery.isFetching ||
        datasetsInFolderQuery.isRefetching) || isMutating;

  const value: DatasetCollectionContextValue = {
    datasets,
    isLoading,
    fetchDatasets,
    clearCacheAndReloadDataset,
    updateCachedDataset,
    activeFolderId,
    setActiveFolderId,
    selectedFolder,
    setSelectedFolder,
    mostRecentlyUsedActiveFolderId,
    isChecking,
    getBreadcrumbs,
    getActiveSubfolders,
    folderModalState,
    setFolderModalState,
    checkDatasets: async (organizationId: string | undefined) => {
      if (isChecking) {
        console.warn("Ignore second rechecking request, since a recheck is already in progress");
        return;
      }
      setIsChecking(true);
      const datastores = await getDatastores();
      await Promise.all(
        datastores.map(
          (
            datastore, // Catch potentially failing triggers, since these should not
          ) =>
            // block the subsequent fetch of datasets. Otherwise, one offline
            // datastore will stop the refresh for all datastores.
            triggerDatasetCheck(datastore.url, organizationId).catch(() => {}),
        ),
      );
      setIsChecking(false);

      datasetsInFolderQuery.refetch();
      datasetSearchQuery.refetch();
    },
    selectedDatasets,
    setSelectedDatasets,
    globalSearchQuery,
    setGlobalSearchQuery,
    searchRecursively,
    setSearchRecursively,
    queries: {
      folderHierarchyQuery,
      datasetsInFolderQuery,
      datasetSearchQuery,
      createFolderMutation,
      deleteFolderMutation,
      updateFolderMutation,
      moveFolderMutation,
      updateDatasetMutation,
    },
    usedStorageInOrga,
  };

  return (
    <DatasetCollectionContext.Provider value={value}>{children}</DatasetCollectionContext.Provider>
  );
}

function useManagedUrlParams(
  setGlobalSearchQuery: (value: string | null) => void,
  setActiveFolderId: React.Dispatch<React.SetStateAction<string | null>>,
  globalSearchQuery: string | null,
  activeFolderId: string | null,
  searchRecursively: boolean,
  setSearchRecursively: (val: boolean) => void,
) {
  const { data: folder } = useFolderQuery(activeFolderId);

  // Read params upon component mount.
  useEffectOnlyOnce(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get("query");
    if (query) {
      setGlobalSearchQuery(query);
    }
    const folderId = params.get("folderId");
    if (folderId) {
      setActiveFolderId(folderId);
    }
    const recursive = params.get("recursive");
    if (recursive != null) setSearchRecursively(recursive === "true");

    const folderSpecifier = last(location.pathname.split("/"));

    if (folderSpecifier?.includes("-")) {
      const nameChunksAndFolderId = folderSpecifier.split("-");
      const folderId = last(nameChunksAndFolderId);
      if (folderId) {
        setActiveFolderId(folderId);
      }
    }
  });

  // Update query and searchRecursively

  // Update folderId
  useEffect(() => {
    if (!globalSearchQuery && activeFolderId) {
      let folderName = folder?.name || "";
      // The replacement of / and space is only done to make the URL
      // nicer to read for a human.
      // encodeURIComponent is used so that special characters, such as # and ?
      // don't break the URL parsing.
      folderName = encodeURIComponent(folderName.replace(/[/ ]+/g, "-"));

      // Use folderName-folderId in path or only folderId if name is empty (e.g., because
      // not loaded yet).
      // Don't use useNavigate because this would lose the input search
      // focus. Keep the existing history state, since react-router stores its own data there.
      window.history.replaceState(
        window.history.state,
        "",
        `/dashboard/datasets/${folderName}${folderName ? "-" : ""}${activeFolderId}`,
      );
    } else {
      const params = new URLSearchParams(location.search);
      if (globalSearchQuery) {
        params.set("query", globalSearchQuery);
      } else {
        params.delete("query");
      }
      if (globalSearchQuery && activeFolderId) {
        params.set("folderId", activeFolderId);
        // The recursive property is only relevant when a folderId is specified.
        if (searchRecursively) {
          params.set("recursive", "true");
        } else {
          params.set("recursive", "false");
        }
      } else {
        params.delete("folderId");
        params.delete("recursive");
      }
      const paramStr = params.toString();

      // Don't use useNavigate because this would lose the input search
      // focus. Keep the existing history state, since react-router stores its own data there.
      window.history.replaceState(
        window.history.state,
        "",
        `/dashboard/datasets${paramStr === "" ? "" : "?"}${paramStr}`,
      );
    }
  }, [globalSearchQuery, activeFolderId, folder, searchRecursively]);
}
